import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiGet, apiPut, apiPost, apiDelete, apiUpload, apiDownload } from '../api/client';
import { formatPhone, formatPhoneInput } from '../utils/formatPhone';
import { parseServerDate } from '../utils/dateUtils';
import type {
  ProfileListResponse,
  ProfileResponse,
  UpdateProfileRequest,
  VaultResponse,
  InviteMemberRequest,
  PendingInvitationResponse,
  AdvisorResponse,
  UpdateAdvisorRequest,
} from '../types/api';
import { VaultRole } from '../types/api';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import Skeleton from './Skeleton';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import EmptyState from './EmptyState';
import DropZone from './DropZone';
import './ProfilesList.css';

interface Props {
  vault: VaultResponse;
  readOnly?: boolean;
  onSave?: () => void;
}

const roleLabels: Record<number, string> = {
  [VaultRole.Owner]: 'Owner',
  [VaultRole.Spouse]: 'Spouse',
  [VaultRole.Witness]: 'Witness',
};

const COMPLETION_FIELDS: { key: keyof ProfileResponse; label: string }[] = [
  { key: 'fullLegalName',  label: 'Full legal name' },
  { key: 'preferredName',  label: 'Preferred name' },
  { key: 'dateOfBirth',    label: 'Date of birth' },
  { key: 'phoneNumber',    label: 'Phone number' },
  { key: 'addressCity',    label: 'City' },
  { key: 'addressState',   label: 'State' },
];

function getCompletionPercent(p: ProfileResponse): number {
  const filled = COMPLETION_FIELDS.filter(f => Boolean(p[f.key])).length;
  return Math.round((filled / COMPLETION_FIELDS.length) * 100);
}

function getMissingFields(p: ProfileResponse): string[] {
  return COMPLETION_FIELDS.filter(f => !p[f.key]).map(f => f.label);
}

function formatCityState(city: string | null, state: string | null): string {
  if (city && state) return `${city}, ${state}`;
  return city || state || '';
}

export default function ProfilesList({ vault, readOnly = false, onSave }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<ProfileResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [completionTipId, setCompletionTipId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoTarget, setPhotoTarget] = useState<string | null>(null);

  // Invite
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<VaultRole>(VaultRole.Spouse);
  const [inviting, setInviting] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitationResponse[]>([]);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resentId, setResentId] = useState<string | null>(null);

  // Advisor state
  const [advisor, setAdvisor] = useState<AdvisorResponse | null>(null);
  const [advisorLoaded, setAdvisorLoaded] = useState(false);
  const [showAdvisorInvite, setShowAdvisorInvite] = useState(false);
  const [advisorInviteEmail, setAdvisorInviteEmail] = useState('');
  const [advisorInviting, setAdvisorInviting] = useState(false);
  const [advisorInviteSent, setAdvisorInviteSent] = useState(false);
  const [showAdvisorEdit, setShowAdvisorEdit] = useState(false);
  const [advisorForm, setAdvisorForm] = useState<UpdateAdvisorRequest>({
    isActive: true, advisorTitle: null, advisorFirm: null, advisorType: null,
  });
  const [advisorSaving, setAdvisorSaving] = useState(false);
  const [advisorError, setAdvisorError] = useState('');

  // Leave vault
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState('');

  // Edit form state
  const [form, setForm] = useState<UpdateProfileRequest>({});

  // Current user's role
  const currentUserEmail = user?.email?.toLowerCase() ?? '';
  const currentMember = vault.members.find(
    m => m.email.toLowerCase() === currentUserEmail
  );
  const isOwner = !readOnly && currentMember?.role === VaultRole.Owner;
  const isAdvisor = currentMember?.role === VaultRole.Advisor;

  // Accepted Owner + Spouse members (excluding self) — shown as contact links to read-only members
  const vaultContacts = vault.members.filter(m =>
    (m.role === VaultRole.Owner || m.role === VaultRole.Spouse) &&
    m.email.toLowerCase() !== currentUserEmail &&
    m.acceptedAt != null
  );

  useEffect(() => {
    if (!completionTipId) return;
    function dismiss() { setCompletionTipId(null); }
    document.addEventListener('click', dismiss);
    return () => document.removeEventListener('click', dismiss);
  }, [completionTipId]);

  const loadProfiles = useCallback(async () => {
    try {
      const [profileData, inviteData] = await Promise.all([
        apiGet<ProfileListResponse>(`/api/vault/${vault.id}/profiles`),
        apiGet<PendingInvitationResponse[]>(`/api/vault/${vault.id}/invitations`),
      ]);
      setProfiles(profileData.profiles);
      setPendingInvites(inviteData);
    } catch (err: any) {
      setError(err.message || 'Failed to load profiles.');
    } finally {
      setLoading(false);
    }
  }, [vault.id]);

  const loadAdvisor = useCallback(async () => {
    try {
      const data = await apiGet<AdvisorResponse>(`/api/vault/${vault.id}/advisor`);
      setAdvisor(data);
    } catch {
      setAdvisor(null); // 404 = no advisor yet
    } finally {
      setAdvisorLoaded(true);
    }
  }, [vault.id]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);
  useEffect(() => { loadAdvisor(); }, [loadAdvisor]);

  // Load photos for profiles that have them
  useEffect(() => {
    profiles
      .filter(p => p.hasPhoto && !photoUrls[p.membershipId])
      .forEach(async (p) => {
        try {
          const { blob } = await apiDownload(
            `/api/vault/${vault.id}/profiles/${p.membershipId}/photo`
          );
          const url = URL.createObjectURL(blob);
          setPhotoUrls(prev => ({ ...prev, [p.membershipId]: url }));
        } catch {
          // Photo load failed silently
        }
      });
  }, [profiles, vault.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function getInitials(p: ProfileResponse): string {
    const first = p.preferredName || p.firstName || '';
    const last = p.lastName || '';
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }

  function formatDate(dateStr: string | null): string {
    return parseServerDate(dateStr)?.toLocaleDateString() ?? '';
  }

  function handleExpand(profile: ProfileResponse) {
    if (expandedId === profile.membershipId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(profile.membershipId);
    setForm({
      fullLegalName: profile.fullLegalName,
      preferredName: profile.preferredName,
      dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : null,
      phoneNumber: profile.phoneNumber,
      addressStreet: profile.addressStreet,
      addressCity: profile.addressCity,
      addressState: profile.addressState,
      addressZip: profile.addressZip,
      notes: profile.notes,
    });
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!expandedId) return;
    setError('');
    setSaving(true);

    try {
      await apiPut(`/api/vault/${vault.id}/profiles/${expandedId}`, form);
      setExpandedId(null);
      await loadProfiles();
      onSave?.();
    } catch (err: any) {
      setError(err.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  }

  function handlePhotoClick(membershipId: string) {
    setPhotoTarget(membershipId);
    photoInputRef.current?.click();
  }

  async function uploadPhoto(membershipId: string, file: File) {
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiUpload(`/api/vault/${vault.id}/profiles/${membershipId}/photo`, formData);

      if (photoUrls[membershipId]) {
        URL.revokeObjectURL(photoUrls[membershipId]);
      }

      const { blob } = await apiDownload(
        `/api/vault/${vault.id}/profiles/${membershipId}/photo`
      );
      const url = URL.createObjectURL(blob);
      setPhotoUrls(prev => ({ ...prev, [membershipId]: url }));

      await loadProfiles();
    } catch (err: any) {
      setError(err.message || 'Photo upload failed.');
    }
  }

  async function handlePhotoChange() {
    const file = photoInputRef.current?.files?.[0];
    if (!file || !photoTarget) return;

    await uploadPhoto(photoTarget, file);
    if (photoInputRef.current) photoInputRef.current.value = '';
    setPhotoTarget(null);
  }

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    setError('');
    setInviting(true);

    try {
      const body: InviteMemberRequest = { email: inviteEmail, role: inviteRole };
      await apiPost(`/api/vault/${vault.id}/invite`, body);
      setInviteEmail('');
      setShowInvite(false);
      await loadProfiles();
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation.');
    } finally {
      setInviting(false);
    }
  }

  async function handleCancelInvite(invitationId: string) {
    setError('');
    try {
      await apiDelete(`/api/vault/${vault.id}/invitations/${invitationId}`);
      await loadProfiles();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel invitation.');
    }
  }

  async function handleResendInvite(invitationId: string) {
    if (resendingId || resentId === invitationId) return;
    setError('');
    setResendingId(invitationId);
    try {
      await apiPost(`/api/vault/${vault.id}/invitations/${invitationId}/resend`);
      await loadProfiles();
      setResentId(invitationId);
      setTimeout(() => setResentId(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend invitation.');
    } finally {
      setResendingId(null);
    }
  }

  async function handleRemoveMember(profile: ProfileResponse) {
    const name = profile.preferredName || `${profile.firstName} ${profile.lastName}`;
    const confirmed = window.confirm(
      `Remove ${name} (${profile.email}) from this vault?\n\nTheir profile and all associated data will be permanently deleted. This cannot be undone.`
    );
    if (!confirmed) return;

    setError('');
    try {
      await apiDelete(`/api/vault/${vault.id}/members/${profile.membershipId}`);
      setExpandedId(null);
      await loadProfiles();
    } catch (err: any) {
      setError(err.message || 'Failed to remove member.');
    }
  }

  async function handleAdvisorInvite(e: FormEvent) {
    e.preventDefault();
    setAdvisorError('');
    setAdvisorInviting(true);
    try {
      await apiPost(`/api/vault/${vault.id}/advisor/invite`, { email: advisorInviteEmail });
      setAdvisorInviteEmail('');
      setShowAdvisorInvite(false);
      setAdvisorInviteSent(true);
      setTimeout(() => setAdvisorInviteSent(false), 4000);
      await loadAdvisor();
    } catch (err: any) {
      setAdvisorError(err.message || 'Failed to invite advisor.');
    } finally {
      setAdvisorInviting(false);
    }
  }

  function handleAdvisorEditOpen() {
    if (!advisor) return;
    setAdvisorForm({
      isActive: advisor.isActive,
      advisorTitle: advisor.advisorTitle,
      advisorFirm: advisor.advisorFirm,
      advisorType: advisor.advisorType,
    });
    setShowAdvisorEdit(true);
  }

  async function handleAdvisorSave(e: FormEvent) {
    e.preventDefault();
    setAdvisorError('');
    setAdvisorSaving(true);
    try {
      const data = await apiPut<AdvisorResponse>(`/api/vault/${vault.id}/advisor`, advisorForm);
      setAdvisor(data);
      setShowAdvisorEdit(false);
    } catch (err: any) {
      setAdvisorError(err.message || 'Failed to update advisor.');
    } finally {
      setAdvisorSaving(false);
    }
  }

  async function handleAdvisorRevoke() {
    const confirmed = window.confirm(
      'Remove this Trusted Advisor from the vault?\n\nThey will lose access immediately. You can invite a new advisor at any time.'
    );
    if (!confirmed) return;
    setAdvisorError('');
    try {
      await apiDelete(`/api/vault/${vault.id}/advisor`);
      setAdvisor(null);
      setShowAdvisorEdit(false);
    } catch (err: any) {
      setAdvisorError(err.message || 'Failed to revoke advisor.');
    }
  }

  async function handleLeaveVault() {
    setLeaveError('');
    setLeaving(true);
    try {
      await apiDelete(`/api/vault/${vault.id}/members/me`);
      navigate('/');
    } catch (err: any) {
      setLeaveError(err.message || 'Failed to leave vault.');
      setLeaving(false);
    }
  }

  if (loading) return <Skeleton variant="card" count={3} />;

  return (
    <div>
      <div className="profiles-header">
        <h3>Member Profiles</h3>
        {isOwner && (
          <button
            className="btn-text"
            onClick={() => setShowInvite(!showInvite)}
          >
            {showInvite ? 'Cancel' : '+ Invite Member'}
          </button>
        )}
      </div>

      <AlertMessage type="error" message={error} />

      {showInvite && (
        <form className="invite-form" onSubmit={handleInvite}>
          <div className="form-group">
            <label className="form-label" htmlFor="inviteEmail">Email address</label>
            <input
              id="inviteEmail"
              className="form-input"
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="email@example.com"
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="inviteRole">Role</label>
            <select
              id="inviteRole"
              className="form-input"
              value={inviteRole}
              onChange={e => setInviteRole(Number(e.target.value) as VaultRole)}
            >
              <option value={VaultRole.Spouse}>Spouse</option>
              <option value={VaultRole.Witness}>Witness</option>
            </select>
          </div>
          <button className="btn-primary" type="submit" disabled={inviting}>
            {inviting ? <LoadingSpinner small /> : 'Send Invitation'}
          </button>
        </form>
      )}

      {isOwner && pendingInvites.length > 0 && (
        <div className="pending-invites">
          <h4 className="pending-invites-title">Pending Invitations</h4>
          {pendingInvites.map(inv => {
            const isExpired = (parseServerDate(inv.expiresAt) ?? new Date(0)) <= new Date();
            return (
              <div key={inv.id} className={`pending-invite-row${isExpired ? ' pending-invite-row--expired' : ''}`}>
                <div className="pending-invite-info">
                  <span className="pending-invite-email">{inv.email}</span>
                  <span className="pending-invite-role">{inv.role}</span>
                  {isExpired && <span className="pending-invite-expired">Expired</span>}
                  {!isExpired && (
                    <span className="pending-invite-expires">
                      Expires {parseServerDate(inv.expiresAt)?.toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="pending-invite-actions">
                  <button
                    className="btn-text"
                    onClick={() => handleResendInvite(inv.id)}
                    disabled={resendingId === inv.id || resentId === inv.id}
                  >
                    {resendingId === inv.id ? 'Sending…' : resentId === inv.id ? 'Sent ✓' : 'Resend'}
                  </button>
                  <button
                    className="btn-text btn-text--danger"
                    onClick={() => handleCancelInvite(inv.id)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <input
        type="file"
        accept="image/*"
        ref={photoInputRef}
        onChange={handlePhotoChange}
        style={{ display: 'none' }}
      />

      {/* Vault contact links — shown to non-owners so they can reach the owner/spouse */}
      {!isOwner && vaultContacts.length > 0 && (
        <div className="vault-contacts">
          <span className="vault-contacts-label">Vault contacts:</span>
          {vaultContacts.map(m => (
            <a
              key={m.membershipId}
              className="vault-contact-link"
              href={`mailto:${m.email}?subject=${encodeURIComponent(`Regarding ${vault.name}`)}`}
            >
              {m.firstName} {m.lastName}
              <span className="vault-contact-role">
                {m.role === VaultRole.Owner ? 'Owner' : 'Spouse'}
              </span>
            </a>
          ))}
        </div>
      )}

      {profiles.length === 0 && !advisorLoaded ? (
        <EmptyState icon="profiles" title="No member profiles yet" />
      ) : profiles.length === 0 && !advisor ? (
        <EmptyState icon="profiles" title="No member profiles yet" />
      ) : profiles.length > 0 ? (
        <div className="profiles-grid">
          {profiles.map(profile => {
            const isExpanded = expandedId === profile.membershipId;
            const isCurrentUser = profile.email?.toLowerCase() === currentUserEmail;
            const canEditProfile = isCurrentUser || isOwner;
            return (
              <DropZone
                key={profile.membershipId}
                onFileDrop={(files) => uploadPhoto(profile.membershipId, files[0])}
                accept="image/*"
                enabled={canEditProfile}
                label="Drop photo"
                className={`profile-card-dropzone${isExpanded ? ' profile-card-dropzone--expanded' : ''}`}
              >
              <div
                className={`profile-card${isExpanded ? ' profile-card--expanded' : ''}`}
                onClick={() => !isExpanded && handleExpand(profile)}
              >
                <div className="profile-card-summary">
                  <div
                    className={`profile-avatar${isExpanded && canEditProfile ? ' profile-avatar--editable' : ''}`}
                    onClick={(e) => {
                      if (isExpanded && canEditProfile) {
                        e.stopPropagation();
                        handlePhotoClick(profile.membershipId);
                      }
                    }}
                  >
                    {photoUrls[profile.membershipId] ? (
                      <img
                        src={photoUrls[profile.membershipId]}
                        alt={profile.preferredName || profile.firstName}
                      />
                    ) : (
                      getInitials(profile)
                    )}
                  </div>
                  <div className="profile-card-details">
                    <div className="profile-card-name">
                      {profile.preferredName || `${profile.firstName} ${profile.lastName}`}
                    </div>
                    <div className="profile-card-meta">
                      <span className="profile-role-badge">
                        {roleLabels[profile.role] || 'Member'}
                      </span>
                      {profile.dateOfBirth && (
                        <span className="profile-card-dob">
                          Born {formatDate(profile.dateOfBirth)}
                        </span>
                      )}
                    </div>
                    {profile.lastLoginAt && (
                      <div className="profile-card-last-login">
                        Last login {formatDate(profile.lastLoginAt)}
                      </div>
                    )}
                    <div className="profile-card-email">{profile.email}</div>
                    {profile.phoneNumber && (
                      <div className="profile-card-phone">
                        <a href={`tel:${profile.phoneNumber}`} onClick={e => e.stopPropagation()}>
                          {formatPhone(profile.phoneNumber)}
                        </a>
                      </div>
                    )}
                    {formatCityState(profile.addressCity, profile.addressState) && (
                      <div className="profile-card-location">
                        {formatCityState(profile.addressCity, profile.addressState)}
                      </div>
                    )}
                  </div>
                </div>
                {(() => {
                  const pct = getCompletionPercent(profile);
                  const missing = pct < 100 ? getMissingFields(profile) : [];
                  const tipOpen = completionTipId === profile.membershipId;
                  return (
                    <div className="profile-completion">
                      <div className="profile-completion-bar">
                        <div
                          className={`profile-completion-fill${pct === 100 ? ' profile-completion-fill--done' : ''}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      {pct < 100 ? (
                        <div className="profile-completion-tip-anchor">
                          <button
                            type="button"
                            className="profile-completion-label profile-completion-label--clickable"
                            onClick={e => {
                              e.stopPropagation();
                              setCompletionTipId(tipOpen ? null : profile.membershipId);
                            }}
                            aria-expanded={tipOpen}
                            aria-label={`${pct}% complete — tap to see what's missing`}
                          >
                            {pct}% complete &#9432;
                          </button>
                          {tipOpen && (
                            <div
                              className="profile-completion-popover"
                              onClick={e => e.stopPropagation()}
                            >
                              <p className="profile-completion-popover-title">To reach 100%, add:</p>
                              <ul className="profile-completion-popover-list">
                                {missing.map(f => <li key={f}>{f}</li>)}
                              </ul>
                              <p className="profile-completion-popover-hint">
                                Tap <strong>Edit profile</strong> below to fill these in.
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="profile-completion-label profile-completion-label--done">
                          Profile complete
                        </span>
                      )}
                    </div>
                  );
                })()}

                {isExpanded && canEditProfile && (
                  <form className="profile-form" onSubmit={handleSave} onClick={e => e.stopPropagation()}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="fullLegalName">Full legal name</label>
                      <input
                        id="fullLegalName"
                        className="form-input"
                        type="text"
                        value={form.fullLegalName || ''}
                        onChange={e => setForm({ ...form, fullLegalName: e.target.value || null })}
                        maxLength={200}
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="preferredName">Preferred name</label>
                      <input
                        id="preferredName"
                        className="form-input"
                        type="text"
                        value={form.preferredName || ''}
                        onChange={e => setForm({ ...form, preferredName: e.target.value || null })}
                        maxLength={100}
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="dateOfBirth">Date of birth</label>
                      <input
                        id="dateOfBirth"
                        className="form-input"
                        type="date"
                        value={form.dateOfBirth || ''}
                        onChange={e => setForm({ ...form, dateOfBirth: e.target.value || null })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="phoneNumber">Phone number</label>
                      <input
                        id="phoneNumber"
                        className="form-input"
                        type="tel"
                        value={form.phoneNumber || ''}
                        onChange={e => setForm({ ...form, phoneNumber: formatPhoneInput(e.target.value) || null })}
                        placeholder="(865) 555-0100"
                        maxLength={14}
                      />
                    </div>
                    <div className="form-group form-group--full">
                      <label className="form-label" htmlFor="addressStreet">Street address</label>
                      <AddressAutocompleteInput
                        id="addressStreet"
                        value={form.addressStreet || ''}
                        onChange={v => setForm({ ...form, addressStreet: v || null })}
                        onAddressSelect={parts => setForm({
                          ...form,
                          addressStreet: parts.street || null,
                          addressCity: parts.city || null,
                          addressState: parts.state || null,
                          addressZip: parts.zip || null,
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="addressCity">City</label>
                      <input
                        id="addressCity"
                        className="form-input"
                        type="text"
                        value={form.addressCity || ''}
                        onChange={e => setForm({ ...form, addressCity: e.target.value || null })}
                        maxLength={100}
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="addressState">State</label>
                      <input
                        id="addressState"
                        className="form-input"
                        type="text"
                        value={form.addressState || ''}
                        onChange={e => setForm({ ...form, addressState: e.target.value || null })}
                        maxLength={50}
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="addressZip">ZIP code</label>
                      <input
                        id="addressZip"
                        className="form-input"
                        type="text"
                        inputMode="numeric"
                        value={form.addressZip || ''}
                        onChange={e => setForm({ ...form, addressZip: e.target.value.replace(/\D/g, '').slice(0, 5) || null })}
                        maxLength={5}
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group form-group--full">
                      <label className="form-label" htmlFor="profileNotes">Notes <span className="form-label-optional">(optional)</span></label>
                      <textarea
                        id="profileNotes"
                        className="form-input"
                        rows={3}
                        value={form.notes || ''}
                        onChange={e => setForm({ ...form, notes: e.target.value || null })}
                        maxLength={5000}
                      />
                    </div>
                    <div className="profile-form-actions">
                      <button className="btn-primary" type="submit" disabled={saving}>
                        {saving ? <LoadingSpinner small /> : 'Save'}
                      </button>
                      <button
                        className="btn-text"
                        type="button"
                        onClick={() => setExpandedId(null)}
                      >
                        Cancel
                      </button>
                      {isOwner && !isCurrentUser && profile.role !== VaultRole.Owner && (
                        <button
                          className="btn-text btn-text--danger"
                          type="button"
                          onClick={() => handleRemoveMember(profile)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </form>
                )}

                {isExpanded && !canEditProfile && (
                  <div className="profile-readonly-detail" onClick={e => e.stopPropagation()}>
                    {profile.fullLegalName && (
                      <div className="profile-readonly-row">
                        <div className="profile-readonly-label">Full Legal Name</div>
                        <div className="profile-readonly-value">{profile.fullLegalName}</div>
                      </div>
                    )}
                    {profile.preferredName && (
                      <div className="profile-readonly-row">
                        <div className="profile-readonly-label">Preferred Name</div>
                        <div className="profile-readonly-value">{profile.preferredName}</div>
                      </div>
                    )}
                    {profile.dateOfBirth && (
                      <div className="profile-readonly-row">
                        <div className="profile-readonly-label">Date of Birth</div>
                        <div className="profile-readonly-value">{formatDate(profile.dateOfBirth)}</div>
                      </div>
                    )}
                    {profile.phoneNumber && (
                      <div className="profile-readonly-row">
                        <div className="profile-readonly-label">Phone</div>
                        <div className="profile-readonly-value">
                          <a href={`tel:${profile.phoneNumber}`}>{formatPhone(profile.phoneNumber)}</a>
                        </div>
                      </div>
                    )}
                    {(profile.addressStreet || profile.addressCity || profile.addressState || profile.addressZip) && (
                      <div className="profile-readonly-row">
                        <div className="profile-readonly-label">Address</div>
                        <div className="profile-readonly-value">
                          {profile.addressStreet && <div>{profile.addressStreet}</div>}
                          <div>
                            {formatCityState(profile.addressCity, profile.addressState)}
                            {profile.addressZip ? ` ${profile.addressZip}` : ''}
                          </div>
                        </div>
                      </div>
                    )}
                    {profile.notes && (
                      <div className="profile-readonly-row">
                        <div className="profile-readonly-label">Notes</div>
                        <div className="profile-readonly-value profile-readonly-value--multiline">{profile.notes}</div>
                      </div>
                    )}
                    <div className="profile-readonly-actions">
                      <button
                        className="btn-text"
                        type="button"
                        onClick={() => setExpandedId(null)}
                      >
                        Close
                      </button>
                      {isOwner && profile.role !== VaultRole.Owner && (
                        <button
                          className="btn-text btn-text--danger"
                          type="button"
                          onClick={() => handleRemoveMember(profile)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </DropZone>
            );
          })}
        </div>
      ) : null}

      {/* Trusted Advisor section — always shown at bottom of Profiles tab */}
      {advisorLoaded && (
        <div className="advisor-section">
          <div className="advisor-section-header">
            <h3 className="advisor-section-title">Trusted Advisor</h3>
            {isOwner && !advisor && (
              <button
                className="btn-text"
                onClick={() => setShowAdvisorInvite(!showAdvisorInvite)}
              >
                {showAdvisorInvite ? 'Cancel' : '+ Invite Advisor'}
              </button>
            )}
          </div>

          <AlertMessage type="error" message={advisorError} />

          {isOwner && showAdvisorInvite && (
            <>
              <p className="advisor-section-desc">
                Invite a licensed professional (attorney, financial planner, etc.) to view your vault. Advisors have read-only access and cannot participate in vault chat or vote on legacy letters.
              </p>
            <form className="invite-form" onSubmit={handleAdvisorInvite}>
              <div className="form-group">
                <label className="form-label" htmlFor="advisorInviteEmail">Advisor's email address</label>
                <input
                  id="advisorInviteEmail"
                  className="form-input"
                  type="email"
                  value={advisorInviteEmail}
                  onChange={e => setAdvisorInviteEmail(e.target.value)}
                  placeholder="advisor@example.com"
                  required
                  autoFocus
                />
              </div>
              <button className="btn-primary" type="submit" disabled={advisorInviting}>
                {advisorInviting ? <LoadingSpinner small /> : 'Send Invitation'}
              </button>
            </form>
            </>
          )}

          {advisorInviteSent && (
            <div className="advisor-invite-sent">
              Invitation sent — check their inbox.
            </div>
          )}

          {!advisor && !showAdvisorInvite && (
            <div className="advisor-empty">
              <p>No Trusted Advisor connected yet.{isOwner ? ' You can invite an estate attorney, financial planner, or other professional to give them read-only access to this vault.' : ''}</p>
            </div>
          )}

          {advisor && !showAdvisorEdit && (
            <div className="advisor-card">
              <div className="advisor-card-header">
                <div className="advisor-avatar">
                  {advisor.firstName.charAt(0)}{advisor.lastName.charAt(0)}
                </div>
                <div className="advisor-card-info">
                  <div className="advisor-card-name">
                    {advisor.firstName} {advisor.lastName}
                  </div>
                  {(advisor.advisorTitle || advisor.advisorFirm) && (
                    <div className="advisor-card-title">
                      {[advisor.advisorTitle, advisor.advisorFirm].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {advisor.advisorType && (
                    <div className="advisor-card-type">{advisor.advisorType}</div>
                  )}
                  <div className="advisor-card-email">{advisor.email}</div>
                  <div className={`advisor-status-badge${advisor.isActive ? '' : ' advisor-status-badge--inactive'}`}>
                    {advisor.isActive
                      ? (advisor.acceptedAt ? 'Active' : 'Pending acceptance')
                      : 'Access paused'}
                  </div>
                </div>
              </div>
              <div className="advisor-card-actions">
                {isOwner && (
                  <a
                    className="btn-text"
                    href={`mailto:${advisor.email}?subject=${encodeURIComponent(`Regarding ${vault.name}`)}`}
                  >
                    Message Advisor
                  </a>
                )}
                {(isOwner || isAdvisor) && (
                  <button className="btn-text" onClick={handleAdvisorEditOpen}>
                    Edit
                  </button>
                )}
                {isOwner && (
                  <button
                    className="btn-text btn-text--danger"
                    onClick={handleAdvisorRevoke}
                  >
                    Revoke Access
                  </button>
                )}
              </div>
            </div>
          )}

          {advisor && (isOwner || isAdvisor) && showAdvisorEdit && (
            <form className="advisor-edit-form" onSubmit={handleAdvisorSave} onClick={e => e.stopPropagation()}>
              {isOwner && (
                <div className="advisor-edit-toggle">
                  <label className="form-label toggle-label">
                    <input
                      type="checkbox"
                      checked={advisorForm.isActive}
                      onChange={e => setAdvisorForm({ ...advisorForm, isActive: e.target.checked })}
                    />
                    Access active (uncheck to temporarily pause)
                  </label>
                </div>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="advisorTitle">Title <span className="form-label-optional">(optional)</span></label>
                <input
                  id="advisorTitle"
                  className="form-input"
                  type="text"
                  value={advisorForm.advisorTitle || ''}
                  onChange={e => setAdvisorForm({ ...advisorForm, advisorTitle: e.target.value || null })}
                  placeholder="Estate Attorney"
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="advisorFirm">Firm / Organization <span className="form-label-optional">(optional)</span></label>
                <input
                  id="advisorFirm"
                  className="form-input"
                  type="text"
                  value={advisorForm.advisorFirm || ''}
                  onChange={e => setAdvisorForm({ ...advisorForm, advisorFirm: e.target.value || null })}
                  placeholder="Smith & Associates"
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="advisorType">Advisor type <span className="form-label-optional">(optional)</span></label>
                <input
                  id="advisorType"
                  className="form-input"
                  type="text"
                  value={advisorForm.advisorType || ''}
                  onChange={e => setAdvisorForm({ ...advisorForm, advisorType: e.target.value || null })}
                  placeholder="Legal, Financial, Medical..."
                  maxLength={100}
                />
              </div>
              <div className="profile-form-actions">
                <button className="btn-primary" type="submit" disabled={advisorSaving}>
                  {advisorSaving ? <LoadingSpinner small /> : 'Save'}
                </button>
                <button className="btn-text" type="button" onClick={() => setShowAdvisorEdit(false)}>
                  Cancel
                </button>
                {isOwner && (
                  <button
                    className="btn-text btn-text--danger"
                    type="button"
                    onClick={handleAdvisorRevoke}
                  >
                    Revoke Access
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}

      {!isOwner && currentMember && (
        <div className="leave-vault-section">
          {!showLeaveConfirm ? (
            <button
              className="btn-text btn-text--danger"
              onClick={() => setShowLeaveConfirm(true)}
            >
              Leave this vault
            </button>
          ) : (
            <div className="leave-vault-confirm">
              <p>Are you sure you want to leave <strong>{vault.name}</strong>? You will lose access immediately.</p>
              <AlertMessage type="error" message={leaveError} />
              <div className="leave-vault-actions">
                <button
                  className="btn-text btn-text--danger"
                  onClick={handleLeaveVault}
                  disabled={leaving}
                >
                  {leaving ? <LoadingSpinner small /> : 'Yes, leave vault'}
                </button>
                <button
                  className="btn-text"
                  onClick={() => { setShowLeaveConfirm(false); setLeaveError(''); }}
                  disabled={leaving}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isOwner && (
        <div className="close-account-section">
          <button
            className="btn-text btn-text--danger"
            onClick={() => navigate(`/vault/${vault.id}/close`)}
          >
            Close this account
          </button>
        </div>
      )}
    </div>
  );
}
